import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Checkbox,
  FormControl,
  Paper,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from '@mui/icons-material/Close';
import api from "../../../services/CBT";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontFamily: "Gilroy-Bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Gilroy-Regular",
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: " rgba(103, 30, 120, 0.1)",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const EditQuestion = ({ onSuccess, id, title, subject, subjectId, questionData }) => {
  const { admToken, user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [formData, setFormData] = useState({
    question: "",
    questionRef: id,
    subjectId: subjectId,
    schoolId: user?.schoolId,
    classId: 0,
    answer: "",
    correctOptions: [],
    setUpId: id,
    cbtType: "",
    correctAnswer: "",
    correctOption: "",
    answerType: "single",
    options: {
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      optionE: "",
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        [name]: value,
      },
    }));
  };

  const handleViewAttachment = (attachment) => {
    setCurrentAttachment(attachment);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentAttachment(null);
  };

  useEffect(() => {
    const fetchQuestionData = async () => {
      try {
        setFetchingData(true);
        const res = await api.getCBTQuestionDetails(admToken, user.schoolId, questionData.questionId);
        if (res.status && res.statusCode === 2) {
          const data = res.data;
          const answerType = data.answerType === "S" ? "single" :
            data.answerType === "M" ? "multiple" : "fill";

          const correctOptions = data.answerType === "M" && data.correctOptions
            ? data.correctOptions.split(',')
            : data.answerType === "S" && data.correctOption
              ? [data.correctOption]
              : [];

          const formattedAttachments = data.attachments?.map(attachment => ({
            id: attachment.id || attachment.attachmentId || 0,
            schoolId: attachment.schoolId || 0,
            fileName: attachment.fileName || "",
            questionRef: attachment.questionRef || "",
            filePath: attachment.filePath || ""
          })) || [];

          const formattedassignClasses = data.assignClasses?.map(classes => ({
            className: classes.className || '',
            classId: classes.classId || 0,
          })) || [];

          setFormData({
            question: data.question || "",
            questionRef: data.questionRef || id,
            subjectId: subjectId,
            schoolId: user?.schoolId,
            classId: 0,
            answer: "",
            correctOptions: correctOptions,
            setUpId: id,
            correctAnswer: data.correctAnswer || "",
            correctOption: data.correctOption || "",
            answerType: answerType,
            cbtType: data?.cbtType,
            options: {
              optionA: data.options?.optionA || "",
              optionB: data.options?.optionB || "",
              optionC: data.options?.optionC || "",
              optionD: data.options?.optionD || "",
              optionE: data.options?.optionE || "",
            },
            attachments: formattedAttachments,
            assignClasses: formattedassignClasses,
          });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load question data");
      } finally {
        setFetchingData(false);
      }
    };

    fetchQuestionData();
  }, [questionData.questionId, admToken, user?.schoolId, subjectId,]);


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const addCBTAttachment = useCallback(async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('SchoolId', user?.schoolId);
      formData.append('FileName', `${title}_${subject}`);
      formData.append('FileContent', selectedFile);
      formData.append('QuestionRef', id);

      const res = await api.addCBTAttachment(admToken, formData);

      if (res.status && res.statusCode === 2) {
        toast.success(res.message || "File uploaded successfully");
      } else {
        toast.error(res.message || "Failed to upload attachment");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload attachment");
    } finally {
      setUploading(false);
    }
  }, [admToken, selectedFile, title, subject, id, user?.schoolId]);

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setLoading(true);
      const res = await api.deleteCBTAttachment(admToken, attachmentId);
      if (res.status && res.statusCode === 2) {
        toast.success(res.message || "File deleted successfully")
        const updatedAttachments = formData.attachments.filter(
          att => att.id !== attachmentId
        );
        setFormData(prev => ({
          ...prev,
          attachments: updatedAttachments
        }));
      } else {
        toast.error(res.message || "Failed to delete attachment");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete attachment");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      let payload = {
        questionId: questionData?.questionId || 0,
        question: formData.question,
        questionRef: formData.questionRef,
        subjectId: formData.subjectId,
        schoolId: formData.schoolId,
        classId: formData.classId,
        setUpId: formData.setUpId,
        answerType: "",
        answer: "",
        correctOptions: "",
        correctOption: "",
        correctAnswer: "",
        options: {
          optionA: formData.options.optionA,
          optionB: formData.options.optionB,
          optionC: formData.options.optionC,
          optionD: formData.options.optionD,
          optionE: formData.options.optionE,
        },
        subject: subject,
        schoolName: user?.schoolName || "",
        cbtType: formData.cbtType,
        title: title,
        attachments: questionData?.attachments || [],
        assignClasses: questionData?.assignClasses || []
      };

      switch (formData.answerType) {
        case "single":
          payload.answerType = "S";
          payload.correctOption = formData.correctOptions[0];
          payload.answer = formData.options[`option${payload.correctOption}`];
          break;

        case "multiple":
          payload.answerType = "M";
          payload.correctOptions = formData.correctOptions.join(",");
          payload.answer = formData.correctOptions
            .map((opt) => formData.options[`option${opt}`])
            .join(", ");
          break;

        case "fill":
          payload.answerType = "F";
          payload.correctAnswer = formData.correctAnswer;
          payload.answer = formData.correctAnswer;
          break;

        default:
          throw new Error("Please select an answer type");
      }

      const res = await api.updateCBTQuestions(admToken, payload);

      if (res.status && res.statusCode === 2) {
        toast.success(res.message || "Question Successfully Updated", {
          autoClose: 4000,
          position: toast.POSITION.BOTTOM_LEFT,
        });
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update question");
    } finally {
      setLoading(false);
    }
  }, [admToken, formData, user?.schoolId, user?.schoolName, id, subjectId, onSuccess, questionData, subject, title]);

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={60} sx={{ color: "#671E78" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <Box>
            <InputLabel
              sx={{
                mb: 1.5,
                mt: 1,
                fontSize: "12px",
                fontWeight: "bolder",
                color: "#000",
                fontFamily: "Gilroy-Medium",
              }}
            >
              Enter Question In the textBox Below
            </InputLabel>
            <TextField
              name="question"
              fullWidth
              size="small"
              value={formData.question}
              onChange={handleChange}
              multiline
              minRows={2}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "1px solid #671E78",
                  },
                  "& textarea": {
                    minHeight: "80px",
                  },
                  padding: "8px",
                },
              }}
              placeholder="Enter your question here"
            />
          </Box>
        </Grid>
        <Grid item lg={12} xs={12}>
          <Box>
            <InputLabel
              sx={{
                mb: 1.5,
                mt: 1,
                fontSize: "12px",
                fontWeight: "bolder",
                color: "#000",
                fontFamily: "Gilroy-Medium",
              }}
            >
              Upload Picture (Optional)
            </InputLabel>

            <input
              type="file"
              id="file-upload"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div className="flex items-center gap-5 w-full">
              <label htmlFor="file-upload" className="w-[80%]">
                <Button
                  variant="outlined"
                  component="span"
                  sx={{
                    border: "1px solid #671E78",
                    color: "#671E78",
                    textTransform: "initial",
                    width: "100%",
                    "&:hover": {
                      border: "1px solid #671E78",
                      backgroundColor: "rgba(103, 30, 120, 0.04)",
                    },
                  }}
                >
                  {selectedFile ? selectedFile.name : "Choose File"}
                </Button>
              </label>
              <Button
                variant="contained"
                sx={{
                  textTransform: "initial",
                  backgroundColor: "#671E78",
                  width: "30%",
                  "&:hover": {
                    backgroundColor: "#4e1661",
                  },
                }}
                onClick={addCBTAttachment}
                disabled={uploading || loading || !selectedFile}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
      {formData.attachments?.length > 0 && (
        <Grid item xs={12}>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-Medium", mb: 2 }}>
              Uploaded Files
            </Typography>
            <TableContainer component={Paper} sx={{ border: "1px solid #671E78" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>S/N</StyledTableCell>
                    <StyledTableCell>File Name</StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.attachments.map((attachment, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>{attachment.fileName}</StyledTableCell>
                      <StyledTableCell>
                        <button
                          className='text-sm border border-[#671E78] px-4 py-1.5 rounded-sm mr-5'
                          onClick={() => handleDeleteAttachment(attachment.id)}
                        >Delete</button>
                        <button
                          className='text-sm border border-[#671E78] px-4 py-1.5 rounded-sm'
                          onClick={() => handleViewAttachment(attachment)}
                        >View</button>
                      </StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      )}

      <Grid item lg={12} xs={12}>
        <Box sx={{ mb: 3, mt: 5 }}>
          <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-Medium", mb: 1 }}>
            Answer Type
          </Typography>
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            backgroundColor: "rgba(103, 30, 120, 0.1)",
            p: "8px"
          }}>
            {["single", "multiple", "fill"].map((type) => (
              <Button
                key={type}
                variant={formData.answerType === type ? "contained" : "outlined"}
                onClick={() =>
                  setFormData({
                    ...formData,
                    answerType: type,
                    correctOptions: type === "single" ? [formData.correctOptions[0] || ""] : [],
                  })
                }
                sx={{
                  textTransform: "initial",
                  borderColor: "#671E78",
                  color: formData.answerType === type ? "#fff" : "#671E78",
                  backgroundColor: formData.answerType === type ? "#671E78" : "transparent",
                  "&:hover": {
                    backgroundColor: formData.answerType === type
                      ? "#4e1661"
                      : "rgba(103, 30, 120, 0.04)",
                  },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {type === "single" && "Single Option"}
                {type === "multiple" && "Multiple Choice"}
                {type === "fill" && "Fill in the Gap"}
              </Button>
            ))}
          </Box>
        </Box>

        {formData.answerType !== "fill" && (
          <TableContainer
            component={Paper}
            sx={{ mb: 3, border: "1px solid #671E78", backgroundColor: "ffffff", boxShadow: "none" }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "ffffff" }}>
                <TableRow>
                  <TableCell sx={{ fontFamily: "Gilroy-Medium", fontWeight: "bold", backgroundColor: "#ffffff" }}>
                    Option
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Gilroy-Medium", fontWeight: "bold", backgroundColor: "#ffffff" }}>
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {["A", "B", "C", "D", "E"].map((option) => (
                  <TableRow key={option} sx={{ backgroundColor: "#ffffff", boxShadow: "none" }}>
                    <TableCell sx={{ fontFamily: "Gilroy-Regular", backgroundColor: "#ffffff" }}>
                      Option {option}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Gilroy-Regular", backgroundColor: "#ffffff" }}>
                      <TextField
                        fullWidth
                        size="small"
                        name={`option${option}`}
                        value={formData.options[`option${option}`] || ""}
                        onChange={handleOptionChange}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { border: "1px solid #671E78" },
                          },
                          backgroundColor: "#ffffff"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: "Gilroy-Medium" }}>
                  {formData.answerType === "multiple"
                    ? "Select Correct Options"
                    : "Select Correct Option"}
                </InputLabel>
                <Select
                  multiple={formData.answerType === "multiple"}
                  value={
                    formData.answerType === "multiple"
                      ? formData.correctOptions
                      : formData.correctOptions[0] || ""
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      correctOptions: formData.answerType === "multiple"
                        ? e.target.value
                        : [e.target.value],
                    });
                  }}
                  sx={{ fontFamily: "Gilroy-Regular" }}
                  size="small"
                  renderValue={
                    formData.answerType === "multiple"
                      ? (selected) => selected.join(", ")
                      : undefined
                  }
                >
                  {["A", "B", "C", "D", "E"].map((option) => (
                    <MenuItem
                      key={option}
                      value={option}
                      sx={{ fontFamily: "Gilroy-Regular" }}
                    >
                      {formData.answerType === "multiple" && (
                        <Checkbox
                          checked={formData.correctOptions.includes(option)}
                          sx={{
                            color: "#671E78",
                            "&.Mui-checked": { color: "#671E78" },
                          }}
                        />
                      )}
                      <ListItemText primary={`Option ${option}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </TableContainer>
        )}

        {formData.answerType === "fill" && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  mb: 1,
                  fontSize: "12px",
                  fontWeight: "bolder",
                  color: "#000",
                  fontFamily: "Gilroy-Medium",
                }}
              >
                Correct Answer
              </InputLabel>
              <TextField
                fullWidth
                size="small"
                name="correctAnswer"
                value={formData.correctAnswer}
                onChange={handleChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { border: "1px solid #671E78" },
                  },
                }}
              />
            </Box>
          </Box>
        )}

        <Box mt={5}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              textTransform: "initial",
              backgroundColor: "#671E78",
              "&:hover": { backgroundColor: "#4e1661" },
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : "Update Question"}
          </Button>
        </Box>
      </Grid>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <Box display="flex" justifyContent="flex-end" p={1}>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          {currentAttachment && (
            <Box display="flex" justifyContent="center">
              {currentAttachment.filePath.match(/\.(jpeg|jpg|gif|png)$/) ? (
                <img
                  src={currentAttachment.filePath}
                  alt={currentAttachment.fileName}
                  style={{ maxHeight: '80vh', maxWidth: '100%' }}
                />
              ) : (
                <iframe
                  src={currentAttachment.filePath}
                  title={currentAttachment.fileName}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EditQuestion;